import { styled } from "@mui/material/styles";
import { TextField, Button, Autocomplete, Box } from "@mui/material";
import { Theme } from "@mui/material/styles";

// Modern TextField with consistent styling
export const StyledTextField = styled(TextField)(
  ({ theme }: { theme: Theme }) => ({
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      borderRadius: "12px",
      transition: "all 0.3s ease-in-out",
      border: "1px solid rgba(90, 100, 119, 0.3)",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 1)",
        transform: "translateY(-2px)",
        boxShadow: "0 4px 12px rgba(90, 100, 119, 0.15)",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(251, 171, 53, 0.6)",
        },
      },
      "&.Mui-focused": {
        backgroundColor: "rgba(255, 255, 255, 1)",
        transform: "translateY(-2px)",
        boxShadow: "0 6px 20px rgba(90, 100, 119, 0.2)",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
          borderWidth: "2px",
        },
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(90, 100, 119, 0.3)",
        borderRadius: "12px",
        transition: "all 0.3s ease-in-out",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(90, 100, 119, 0.8)",
      fontWeight: 500,
      "&.Mui-focused": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1.5),
      fontSize: "1rem",
      "&::placeholder": {
        color: "rgba(90, 100, 119, 0.5)",
        opacity: 1,
      },
    },
  }),
);

// Modern Autocomplete with consistent styling
export const StyledAutocomplete = styled(Autocomplete)<any>(
  ({ theme }: { theme: Theme }) => ({
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      borderRadius: theme.spacing(1),
      transition: "all 0.3s ease-in-out",
      border: "1px solid rgba(90, 100, 119, 0.3)",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 1)",
        transform: "translateY(-2px)",
        boxShadow: "0 4px 12px rgba(90, 100, 119, 0.15)",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(251, 171, 53, 0.6)",
        },
      },
      "&.Mui-focused": {
        backgroundColor: "rgba(255, 255, 255, 1)",
        transform: "translateY(-2px)",
        boxShadow: "0 6px 20px rgba(90, 100, 119, 0.2)",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
          borderWidth: "2px",
        },
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(90, 100, 119, 0.3)",
        transition: "all 0.3s ease-in-out",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(90, 100, 119, 0.8)",
      fontWeight: 500,
      "&.Mui-focused": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiAutocomplete-popupIndicator": {
      color: "rgba(90, 100, 119, 0.7)",
      "&:hover": {
        backgroundColor: "rgba(251, 171, 53, 0.1)",
      },
    },
    "& .MuiAutocomplete-clearIndicator": {
      color: "rgba(90, 100, 119, 0.7)",
      "&:hover": {
        backgroundColor: "rgba(251, 171, 53, 0.1)",
      },
    },
  }),
);

// Modern Form Button
export const StyledFormButton = styled(Button)(
  ({ theme }: { theme: Theme }) => ({
    borderRadius: "12px",
    padding: theme.spacing(1.5, 3),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "1rem",
    transition: "all 0.3s ease-in-out",
    border: "2px solid transparent",
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, rgba(90, 100, 119, 0.8) 100%)`,
    color: "#FFFFFF",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(90, 100, 119, 0.3)",
      background: `linear-gradient(135deg, rgba(90, 100, 119, 0.9) 0%, ${theme.palette.primary.main} 100%)`,
    },
    "&:active": {
      transform: "translateY(0)",
      boxShadow: "0 2px 8px rgba(90, 100, 119, 0.2)",
    },
    "&.MuiButton-outlined": {
      background: "transparent",
      border: `2px solid ${theme.palette.primary.main}`,
      color: theme.palette.primary.main,
      "&:hover": {
        background: `linear-gradient(135deg, rgba(251, 171, 53, 0.1) 0%, rgba(90, 100, 119, 0.1) 100%)`,
        borderColor: theme.palette.secondary.main,
        color: theme.palette.secondary.main,
      },
    },
  }),
);

// Form Container with modern styling
export const StyledFormContainer = styled(Box)(
  ({ theme }: { theme: Theme }) => ({
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    border: "1px solid rgba(90, 100, 119, 0.2)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 8px 32px rgba(90, 100, 119, 0.1)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      boxShadow: "0 12px 40px rgba(90, 100, 119, 0.15)",
      transform: "translateY(-2px)",
    },
  }),
);

// Form Section with divider
export const StyledFormSection = styled(Box)(({ theme }: { theme: Theme }) => ({
  marginBottom: theme.spacing(3),
  "& .MuiTypography-h6": {
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  "& .MuiDivider-root": {
    marginBottom: theme.spacing(2),
    backgroundColor: "rgba(90, 100, 119, 0.2)",
  },
}));

// Utility function for form field styling
export const getFormFieldStyles = (width?: string) => ({
  width: width || "100%",
  "& .MuiFormHelperText-root": {
    color: "rgba(90, 100, 119, 0.7)",
    fontSize: "0.75rem",
  },
  "& .MuiFormHelperText-root.Mui-error": {
    color: "#d32f2f",
  },
});

// Modern field spacing
export const fieldSpacing = {
  marginBottom: 2,
};

// Form validation styles
export const validationStyles = {
  error: {
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d32f2f",
        borderWidth: "2px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d32f2f",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d32f2f",
      },
    },
  },
  success: {
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#2e7d32",
        borderWidth: "2px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#2e7d32",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#2e7d32",
      },
    },
  },
};
